import * as React from 'react';
import ActivationOpsSections from './jobs/ActivationOpsSections';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment-timezone';
import useActivation, {
    ActivationObject,
} from '@/src/Activations/components/hooks/useActivation';
import { useEventContext } from '../components/EventContext';
import { eventDay, photosUploaded, shareRate } from '../lib/eventStats';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import { toast } from 'sonner';
import useProcessing from '@/src/Processing/components/hooks/useProcessing';
import useExperienceDefinition from '@/src/ExperienceBuilder/hooks/useExperienceDefinition';
import useEditActivation from '@/src/Activations/components/hooks/useEditActivation';
import useActivationOrgReassignment from '@/src/Activations/components/hooks/useActivationOrgReassignment';
import useOrgs from '@/src/Orgs/components/hooks/useOrgs';
import FeatureFlagContext from '@/src/Features/components/FeatureFlagContext';
import { FeatureFlags } from '@/src/Features/lib/constants';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import HeroChart, {
    AxisTick,
    ChartSeries,
} from '../components/HeroChart';
import EventDevices from '../components/EventDevices';

/*
 * Overview v2 — the state-aware event landing. Posture is DERIVED, never
 * declared: live = a capture in the last 45 min (the Home rule), pre =
 * event date ahead, post = date passed and quiet. The main column owns
 * posture content (readiness checklist / live ops / results); the right
 * rail persists across all three and is the ONLY place numbers render
 * (capability-gated tiles, gallery links, drill-downs) — duplicating
 * them elsewhere recreates the drift the IA audit found.
 */

const IconExternal = ({ className = 'h-[13px] w-[13px]' }) => (
    <svg
        viewBox="0 0 20 20"
        className={className}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M8 4H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-3M12 4h4v4M16 4l-7 7" />
    </svg>
);

const IconCopy = () => (
    <svg
        viewBox="0 0 20 20"
        className="h-[13px] w-[13px]"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect x="7" y="7" width="9" height="9" rx="2" />
        <path d="M13 7V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h1" />
    </svg>
);

/* one readiness fact: its own truth + a link to its fix — no verdict */
function ChecklistRow({
    tone,
    name,
    meta,
    action,
    onGo,
}: {
    tone: 'good' | 'warn' | 'neutral';
    name: string;
    meta: React.ReactNode;
    action: string;
    onGo: () => void;
}) {
    return (
        <div className="flex items-center gap-3 border-t border-line py-[10px] text-[12.5px] first:border-t-0">
            <span
                className={
                    'h-[7px] w-[7px] flex-none rounded-full ' +
                    (tone === 'good'
                        ? 'bg-good'
                        : tone === 'warn'
                        ? 'bg-attention'
                        : 'bg-off')
                }
            />
            <span className="w-[82px] flex-none font-semibold">{name}</span>
            <span
                className={
                    'min-w-0 flex-1 ' +
                    (tone === 'warn'
                        ? 'font-semibold text-attention'
                        : 'text-muted-foreground')
                }
            >
                {meta}
            </span>
            <button
                type="button"
                onClick={onGo}
                className="cursor-pointer whitespace-nowrap border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-brand-ink"
            >
                {action} →
            </button>
        </div>
    );
}

interface SessionRow {
    id: string | number;
    photoSessionId?: string | number;
    date: string;
    createdAt?: string;
    total: number;
    destination?: string;
}

export default function OverviewV2() {
    const { activationId } = useParams();
    const id = activationId ? +activationId : 0;
    const navigate = useNavigate();
    const { unarchiveActivation } = useActivation();
    const { reassignActivationOrg } = useActivationOrgReassignment();
    const { listOrgs } = useOrgs();
    const featureFlags = useContext(FeatureFlagContext) as any;
    const canReassign = !!featureFlags?.[FeatureFlags.ACTIVATION_ORG_SWAP_V1];
    const {
        activation: ctxActivation,
        gallery: ctxGallery,
        domain: ctxDomain,
        capabilities,
        assignedDeviceCount,
        assignedGroupCount,
        resolved,
        ensureSessions,
        ensureShares,
        ensureMedia,
        media: ctxMedia,
    } = useEventContext();
    const { org } = useUserOrg();
    const authenticatedApi = useAuthenticatedApi();
    const { saveActivation } = useEditActivation(id);
    const { getDefinition } = useExperienceDefinition();
    const { getProcessingFlows } = useProcessing();

    const [activation, setActivation] = useState<ActivationObject | null>(
        null,
    );
    const [sessions, setSessions] = useState<SessionRow[]>([]);
    const [shares, setShares] = useState<any[]>([]);
    const [galleries, setGalleries] = useState<any[]>([]);
    const [galleryUrl, setGalleryUrl] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState({
        name: '',
        date: '',
        location: '',
        v3Folder: '',
    });
    const [reassignOpen, setReassignOpen] = useState(false);
    const [reassignOrgs, setReassignOrgs] = useState<any[] | null>(null);
    const [reassignTarget, setReassignTarget] = useState('');
    const [reassignBusy, setReassignBusy] = useState(false);
    const [isSavingDetails, setIsSavingDetails] = useState(false);
    const [copied, setCopied] = useState<string | null>(null);

    const copyLink = (key: string, text: string) => {
        navigator.clipboard?.writeText(text).then(() => {
            setCopied(key);
            setTimeout(() => setCopied(null), 1500);
        });
    };

    /* client report magic link — the server signs the token; the
       page at /report/:token is public and aggregate-only */
    const copyClientReportLink = async () => {
        const token = await authenticatedApi
            .get(`/activations/${id}/reportLink`)
            .then((r) => r.data?.token as string)
            .catch(() => null);
        if (!token) {
            toast.error("Couldn't create the client link.");
            return;
        }
        const url = `${window.location.origin}/report/${token}`;
        try {
            await navigator.clipboard.writeText(url);
            toast.success('Client link copied');
        } catch {
            window.prompt('Copy the client link:', url);
        }
    };
    const [detailsError, setDetailsError] = useState(false);
    /* pre-event checklist facts (null = not yet checked) */
    const [hasExperience, setHasExperience] = useState<boolean | null>(null);
    const [flowsEnabled, setFlowsEnabled] = useState<number | null>(null);
    const devicesRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const [s, sh] = await Promise.all([
                    ensureSessions(),
                    ensureShares(),
                ]);
                /* canonical Photos rides the shared media cache (also
                   used by the Media page — one fetch per event visit) */
                ensureMedia();
                if (!mounted) return;
                setActivation(ctxActivation);
                setSessions(s ?? []);
                setShares(sh ?? []);
                setGalleries(ctxGallery ? [ctxGallery] : []);
                setGalleryUrl(ctxDomain);
            } catch (e) {
                /* partial data is fine for v1 */
            } finally {
                if (mounted) setIsLoading(false);
            }
        };
        if (resolved) load();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, resolved]);

    /* both endpoints LEFT JOIN one-to-many tables, so a session with two
       shares (or a share with two media rows) comes back duplicated —
       dedupe before counting or charting */
    const uniqueSessions = useMemo(() => {
        const seen = new Set<string | number>();
        return sessions.filter((s) => {
            const key = s.photoSessionId ?? s.id;
            if (key == null) return true;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [sessions]);

    const uniqueShares = useMemo(() => {
        const seen = new Set<string | number>();
        return shares.filter((s: any) => {
            if (s.id == null) return true;
            if (seen.has(s.id)) return false;
            seen.add(s.id);
            return true;
        });
    }, [shares]);

    const kpis = useMemo(() => {
        /* canonical Photos (lib/eventStats) — falls back to the session
           estimate until the media cache lands */
        const photos =
            ctxMedia != null
                ? photosUploaded(ctxMedia)
                : uniqueSessions.reduce(
                      (sum, s) => sum + (Number(s.total) || 0),
                      0,
                  );
        const unsent = uniqueShares.filter((s: any) => !s.sent).length;
        /* Views is first-gallery scoped everywhere (one activation =
           one gallery) */
        const views = Number(galleries[0]?.totalViews) || 0;
        return {
            photos,
            sessions: uniqueSessions.length,
            shares: uniqueShares.length,
            unsent,
            views,
        };
    }, [uniqueSessions, uniqueShares, galleries, ctxMedia]);

    /* posture: live = capture <45 min (the Home rule); a same-day event
       with photos stays live between bursts; otherwise the date decides */
    const posture = useMemo<'pre' | 'live' | 'post'>(() => {
        const now = moment();
        const latest = uniqueSessions.reduce(
            (max, s) =>
                Math.max(max, moment(s.createdAt ?? s.date).valueOf()),
            0,
        );
        if (latest > 0 && now.valueOf() - latest < 45 * 60 * 1000)
            return 'live';
        const dd = activation?.date ? eventDay(activation.date) : null;
        if (dd?.isValid()) {
            if (dd.isSame(now, 'day'))
                return uniqueSessions.some((s) =>
                    moment(s.createdAt ?? s.date).isSame(now, 'day'),
                )
                    ? 'live'
                    : 'pre';
            return dd.isAfter(now, 'day') ? 'pre' : 'post';
        }
        return uniqueSessions.length > 0 ? 'post' : 'pre';
    }, [uniqueSessions, activation]);

    const liveStats = useMemo(() => {
        const now = moment();
        const todays = uniqueSessions.filter((s) =>
            moment(s.createdAt ?? s.date).isSame(now, 'day'),
        );
        const rows = todays.length > 0 ? todays : uniqueSessions;
        const first = rows.reduce(
            (min, s) =>
                Math.min(min, moment(s.createdAt ?? s.date).valueOf()),
            Infinity,
        );
        const latest = rows.reduce(
            (max, s) =>
                Math.max(max, moment(s.createdAt ?? s.date).valueOf()),
            0,
        );
        const last30 = uniqueSessions
            .filter(
                (s) =>
                    now.valueOf() - moment(s.createdAt ?? s.date).valueOf() <
                    30 * 60 * 1000,
            )
            .reduce((sum, s) => sum + (Number(s.total) || 0), 0);
        const active =
            latest > 0 && now.valueOf() - latest < 45 * 60 * 1000;
        let elapsed = '';
        if (Number.isFinite(first)) {
            const mins = Math.max(
                0,
                Math.round((now.valueOf() - first) / 60000),
            );
            elapsed =
                mins >= 60
                    ? `${Math.floor(mins / 60)}h ${mins % 60}m`
                    : `${mins}m`;
        }
        return { elapsed, last30, active };
    }, [uniqueSessions]);

    const rate = useMemo(
        () => shareRate(uniqueSessions, uniqueShares),
        [uniqueSessions, uniqueShares],
    );

    /* photo strips ride the shared media cache — same rows as Media */
    const mediaStrip = useMemo(() => {
        const rows = (ctxMedia ?? []).filter(
            (m: any) =>
                !m.isHidden &&
                (m.smileportType === 'processed' ||
                    m.smileportType === 'processed-video'),
        );
        const sorted = [...rows].sort(
            (a: any, b: any) =>
                moment(b.capturedAt ?? b.createdAt ?? 0).valueOf() -
                moment(a.capturedAt ?? a.createdAt ?? 0).valueOf(),
        );
        const favorites = sorted.filter((m: any) => m.isFavorite);
        return {
            latest: sorted.slice(0, 6),
            favorites: favorites.slice(0, 8),
            favoriteCount: favorites.length,
        };
    }, [ctxMedia]);

    /* readiness facts — fetched only while the checklist is showing */
    useEffect(() => {
        if (isLoading || posture !== 'pre') return;
        let mounted = true;
        (async () => {
            if (hasExperience == null) {
                const def = await getDefinition(id).catch(() => null);
                if (mounted) setHasExperience(!!def?.definition);
            }
            if (flowsEnabled == null && ctxGallery?.id != null) {
                const flows = await getProcessingFlows(
                    ctxGallery.id,
                ).catch(() => []);
                if (mounted)
                    setFlowsEnabled(
                        (flows ?? []).filter((f: any) => f.enabled).length,
                    );
            }
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, posture, id]);

    /* hero chart: cumulative photos + shares, bucketed hourly for
       single-day events and daily for longer ones */
    const chart = useMemo(() => {
        const events = (
            rows: any[],
            value: (r: any) => number,
            ts: (r: any) => any,
        ) =>
            rows
                .map((r) => ({ t: moment(ts(r)).valueOf(), v: value(r) }))
                .filter((e) => Number.isFinite(e.t) && e.t > 0);

        const photoEvents = events(
            uniqueSessions,
            (s) => Number(s.total) || 0,
            (s) => s.createdAt ?? s.date,
        );
        const shareEvents = events(
            uniqueShares,
            () => 1,
            (s) => s.createdAt ?? s.created_at,
        );
        const all = [...photoEvents, ...shareEvents];
        if (all.length === 0) return null;

        const minT = Math.min(...all.map((e) => e.t));
        const maxT = Math.max(...all.map((e) => e.t));
        const unit: 'hour' | 'day' =
            maxT - minT <= 48 * 3600 * 1000 ? 'hour' : 'day';

        const buckets: number[] = [];
        const indexOf = new Map<number, number>();
        const cursor = moment(minT).startOf(unit);
        const last = moment(maxT).startOf(unit).valueOf();
        while (cursor.valueOf() <= last && buckets.length < 500) {
            indexOf.set(cursor.valueOf(), buckets.length);
            buckets.push(cursor.valueOf());
            cursor.add(1, unit);
        }
        if (buckets.length < 2) return null;

        const cumulative = (evts: { t: number; v: number }[]) => {
            const per = buckets.map(() => 0);
            evts.forEach((e) => {
                const i = indexOf.get(moment(e.t).startOf(unit).valueOf());
                per[i ?? buckets.length - 1] += e.v;
            });
            let run = 0;
            return per.map((v) => (run += v));
        };

        const series: ChartSeries[] = [
            {
                label: 'Photos',
                color: 'hsl(var(--brand))',
                values: cumulative(photoEvents),
            },
        ];
        if (shareEvents.length > 0) {
            series.push({
                label: 'Shares',
                color: 'hsl(var(--attention))',
                values: cumulative(shareEvents),
            });
        }

        const fresh = Date.now() - maxT < 3600 * 1000;
        const axis: AxisTick[] = [0, 1, 2, 3, 4].map((k) => {
            const i = Math.round((k * (buckets.length - 1)) / 4);
            const isLast = i === buckets.length - 1;
            return {
                label:
                    isLast && fresh
                        ? 'now'
                        : moment(buckets[i]).format(
                              unit === 'hour' ? 'h A' : 'MMM D',
                          ),
                strong: isLast && fresh,
            };
        });

        return { series, axis };
    }, [uniqueSessions, uniqueShares]);

    const d = activation?.date ? eventDay(activation.date) : null;

    const startEditing = () => {
        if (!activation) return;
        setDraft({
            name: activation.name ?? '',
            /* eventDay, or the picker prefills a day early and a
               no-change save silently shifts the date back */
            date: eventDay(activation.date).format('YYYY-MM-DD'),
            location: activation.location ?? '',
            v3Folder: (activation as any).v3Folder ?? '',
        });
        setReassignOpen(false);
        setDetailsError(false);
        setIsEditing(true);
    };

    const saveDetails = async () => {
        if (!activation || isSavingDetails || !draft.name.trim()) return;
        setIsSavingDetails(true);
        setDetailsError(false);
        const ok = await saveActivation({
            name: draft.name.trim(),
            date: draft.date,
            location: draft.location.trim(),
            v3Folder: draft.v3Folder.trim() || null,
        }).catch(() => false);
        setIsSavingDetails(false);
        if (ok) {
            setActivation({
                ...activation,
                name: draft.name.trim(),
                date: draft.date as any,
                location: draft.location.trim(),
                v3Folder: draft.v3Folder.trim(),
            } as any);
            setIsEditing(false);
        } else {
            setDetailsError(true);
        }
    };

    if (isLoading) {
        return (
            <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
                {[0, 1, 2].map((i) => (
                    <div
                        key={i}
                        className="mb-4 h-16 animate-pulse rounded-[12px] bg-secondary"
                    />
                ))}
            </main>
        );
    }

    return (
        <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-2">Overview</div>
            {/* title block — the page owns the event name; the crumbs are
                gone, so the eyebrow is the wayfinding. Details edit inline
                here — the classic details tab's day job. */}
            {isEditing ? (
                <div className="mb-9 mt-2 max-w-[560px]">
                    <input
                        autoFocus
                        className="ps-input w-full text-[23px] font-bold leading-tight tracking-[-0.02em]"
                        value={draft.name}
                        placeholder="Activation name"
                        onChange={(e) =>
                            setDraft((v) => ({ ...v, name: e.target.value }))
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') saveDetails();
                            if (e.key === 'Escape') setIsEditing(false);
                        }}
                    />
                    <div className="mt-3 flex flex-wrap items-end gap-x-5 gap-y-2">
                        <div>
                            <label className="mb-1 block text-[11px] font-semibold text-muted-foreground">
                                Date
                            </label>
                            <input
                                type="date"
                                className="ps-input text-[13px]"
                                value={draft.date}
                                onChange={(e) =>
                                    setDraft((v) => ({
                                        ...v,
                                        date: e.target.value,
                                    }))
                                }
                            />
                        </div>
                        <div className="min-w-[200px] flex-1">
                            <label className="mb-1 block text-[11px] font-semibold text-muted-foreground">
                                Location
                            </label>
                            <input
                                className="ps-input w-full text-[13px]"
                                value={draft.location}
                                placeholder="Venue, city"
                                onChange={(e) =>
                                    setDraft((v) => ({
                                        ...v,
                                        location: e.target.value,
                                    }))
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveDetails();
                                    if (e.key === 'Escape')
                                        setIsEditing(false);
                                }}
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-[11px] font-semibold text-muted-foreground">
                                v3 folder{' '}
                                <span className="font-normal text-faint">
                                    — optional
                                </span>
                            </label>
                            <input
                                className="ps-input w-[180px] font-mono text-[12px]"
                                value={draft.v3Folder}
                                onChange={(e) =>
                                    setDraft((v) => ({
                                        ...v,
                                        v3Folder: e.target.value,
                                    }))
                                }
                            />
                        </div>
                        <div className="flex items-center gap-2.5">
                            {detailsError && (
                                <span className="text-[11.5px] font-semibold text-attention">
                                    Save failed
                                </span>
                            )}
                            <button
                                type="button"
                                onClick={saveDetails}
                                disabled={isSavingDetails || !draft.name.trim()}
                                className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-brand-foreground disabled:opacity-50"
                            >
                                {isSavingDetails ? 'Saving…' : 'Save'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsEditing(false)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-muted-foreground hover:text-foreground"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                    {canReassign && (
                        <div className="mt-3">
                            {reassignOpen ? (
                                <div className="flex flex-wrap items-center gap-2.5">
                                    <select
                                        className="ps-input w-[220px] cursor-pointer appearance-none text-[12.5px]"
                                        value={reassignTarget}
                                        onChange={(e) =>
                                            setReassignTarget(e.target.value)
                                        }
                                    >
                                        <option value="">
                                            Move to organization…
                                        </option>
                                        {(reassignOrgs ?? [])
                                            .filter(
                                                (o: any) =>
                                                    o.id !== org?.id,
                                            )
                                            .map((o: any) => (
                                                <option
                                                    key={o.id}
                                                    value={o.id}
                                                >
                                                    {o.name}
                                                </option>
                                            ))}
                                    </select>
                                    <button
                                        type="button"
                                        disabled={
                                            !reassignTarget || reassignBusy
                                        }
                                        onClick={async () => {
                                            if (
                                                !window.confirm(
                                                    'Move this activation — with its gallery, media, and shares — to the selected organization?',
                                                )
                                            )
                                                return;
                                            setReassignBusy(true);
                                            const r =
                                                await reassignActivationOrg(
                                                    id,
                                                    +reassignTarget,
                                                );
                                            setReassignBusy(false);
                                            if (r?.success)
                                                navigate(
                                                    '/v2/activations',
                                                );
                                        }}
                                        className="cursor-pointer rounded-full border-0 bg-foreground px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-background disabled:opacity-50"
                                    >
                                        {reassignBusy
                                            ? 'Moving…'
                                            : 'Reassign'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setReassignOpen(false)
                                        }
                                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-muted-foreground"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={async () => {
                                        setReassignOpen(true);
                                        if (reassignOrgs == null)
                                            setReassignOrgs(
                                                await listOrgs().catch(
                                                    () => [],
                                                ),
                                            );
                                    }}
                                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-faint hover:text-attention"
                                >
                                    Reassign organization…
                                </button>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <div className="group mb-9 mt-2">
                    <h1 className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[23px] font-bold leading-tight tracking-[-0.02em]">
                        {activation?.name ?? 'Activation'}
                        {activation?.isArchived && (
                            <button
                                type="button"
                                title="Unarchive this activation"
                                onClick={async () => {
                                    if (
                                        !window.confirm(
                                            'Unarchive this activation?',
                                        )
                                    )
                                        return;
                                    const ok = await unarchiveActivation(
                                        id,
                                    ).catch(() => false);
                                    if (ok)
                                        setActivation((a: any) =>
                                            a
                                                ? {
                                                      ...a,
                                                      isArchived: false,
                                                  }
                                                : a,
                                        );
                                }}
                                className="cursor-pointer rounded-full border-0 bg-secondary px-2 py-0.5 font-[inherit] text-[10.5px] font-semibold text-muted-foreground hover:text-foreground"
                            >
                                archived · unarchive
                            </button>
                        )}
                        <button
                            type="button"
                            title="Edit details"
                            onClick={startEditing}
                            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent text-faint opacity-0 transition-opacity hover:bg-secondary hover:text-foreground focus:opacity-100 group-hover:opacity-100"
                        >
                            <svg
                                viewBox="0 0 20 20"
                                className="h-[15px] w-[15px]"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.7"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M13.5 3.5 16.5 6.5 7 16H4v-3z" />
                            </svg>
                        </button>
                    </h1>
                    <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2.5 gap-y-1 text-[13px] text-muted-foreground">
                        {d && <span>{d.format('MMM D, YYYY')}</span>}
                        {activation?.location && (
                            <>
                                <span className="text-faint">·</span>
                                <span>{activation.location}</span>
                            </>
                        )}
                        {activation?.owner && (
                            <>
                                <span className="text-faint">·</span>
                                <span>Lead: {activation.owner}</span>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* posture status line — derived, never declared */}
            <div className="mb-8 flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.1em]">
                {posture === 'live' ? (
                    <>
                        <span
                            className={
                                liveStats.active
                                    ? 'ps-pulse ps-pulse-live'
                                    : 'h-[7px] w-[7px] flex-none rounded-full bg-attention'
                            }
                        />
                        <span className="text-attention">
                            Live
                            {liveStats.elapsed
                                ? ` · ${liveStats.elapsed}`
                                : ''}
                            {liveStats.active && liveStats.last30 > 0
                                ? ` · ${liveStats.last30.toLocaleString()} photos last 30 min`
                                : ''}
                        </span>
                    </>
                ) : (
                    <>
                        <span className="h-[7px] w-[7px] flex-none rounded-full bg-off" />
                        <span className="text-faint">
                            {posture === 'pre'
                                ? `Upcoming${
                                      d?.isValid()
                                          ? d.isSame(moment(), 'day')
                                              ? ' · today'
                                              : ` · in ${d
                                                    .clone()
                                                    .startOf('day')
                                                    .diff(
                                                        moment().startOf(
                                                            'day',
                                                        ),
                                                        'days',
                                                    )} days`
                                          : ''
                                  }`
                                : `Wrapped${
                                      d?.isValid()
                                          ? ` · ${d.format('MMM D')}`
                                          : ''
                                  }`}
                        </span>
                    </>
                )}
            </div>

            <div className="grid gap-x-12 gap-y-10 md:grid-cols-[minmax(0,1fr)_280px]">
                {/* main column — posture content only, never stats */}
                <div className="min-w-0">
                    {posture === 'pre' && (
                        <>
                            <section className="mb-10">
                                <div className="ps-label mb-2">
                                    {d?.isValid()
                                        ? `Ready for ${d.format('dddd')}?`
                                        : 'Readiness'}
                                </div>
                                <ChecklistRow
                                    tone={
                                        capabilities.hasGallery
                                            ? 'good'
                                            : 'warn'
                                    }
                                    name="Gallery"
                                    meta={
                                        capabilities.hasGallery
                                            ? `configured${
                                                  ctxGallery?.slug
                                                      ? ` · ${ctxGallery.slug}`
                                                      : ''
                                              }`
                                            : 'none — captures have nowhere to land'
                                    }
                                    action={
                                        capabilities.hasGallery
                                            ? 'Gallery'
                                            : 'Create'
                                    }
                                    onGo={() =>
                                        capabilities.hasGallery
                                            ? navigate(
                                                  `/v2/activations/${id}/gallery`,
                                              )
                                            : navigate(
                                                  `/activations/${id}/galleries`,
                                              )
                                    }
                                />
                                <ChecklistRow
                                    tone={
                                        assignedDeviceCount +
                                            assignedGroupCount >
                                        0
                                            ? 'good'
                                            : 'warn'
                                    }
                                    name="Devices"
                                    meta={
                                        assignedDeviceCount +
                                            assignedGroupCount >
                                        0
                                            ? [
                                                  assignedDeviceCount > 0 &&
                                                      `${assignedDeviceCount} device${
                                                          assignedDeviceCount ===
                                                          1
                                                              ? ''
                                                              : 's'
                                                      }`,
                                                  assignedGroupCount > 0 &&
                                                      `${assignedGroupCount} group${
                                                          assignedGroupCount ===
                                                          1
                                                              ? ''
                                                              : 's'
                                                      }`,
                                              ]
                                                  .filter(Boolean)
                                                  .join(' + ') + ' assigned'
                                            : 'none assigned yet'
                                    }
                                    action="Assign"
                                    onGo={() =>
                                        devicesRef.current?.scrollIntoView({
                                            behavior: 'smooth',
                                            block: 'start',
                                        })
                                    }
                                />
                                <ChecklistRow
                                    tone={hasExperience ? 'good' : 'neutral'}
                                    name="Experience"
                                    meta={
                                        hasExperience == null
                                            ? 'checking…'
                                            : hasExperience
                                            ? 'configured'
                                            : 'none — required only for clickerbooth'
                                    }
                                    action={
                                        hasExperience ? 'Capture' : 'Set up'
                                    }
                                    onGo={() =>
                                        navigate(
                                            `/v2/activations/${id}/capture`,
                                        )
                                    }
                                />
                                <ChecklistRow
                                    tone={
                                        capabilities.sharingOn
                                            ? 'good'
                                            : 'neutral'
                                    }
                                    name="Sharing"
                                    meta={
                                        capabilities.sharingOn
                                            ? 'on · ' +
                                              [
                                                  ctxGallery?.emailEnabled &&
                                                      'email',
                                                  ctxGallery?.textEnabled &&
                                                      'text',
                                              ]
                                                  .filter(Boolean)
                                                  .join(' + ')
                                            : 'off — guests browse the gallery only (off is a valid choice)'
                                    }
                                    action={
                                        capabilities.sharingOn
                                            ? 'Sharing'
                                            : 'Enable'
                                    }
                                    onGo={() =>
                                        navigate(
                                            `/v2/activations/${id}/sharing`,
                                        )
                                    }
                                />
                                {capabilities.hasGallery && (
                                    <ChecklistRow
                                        tone={
                                            flowsEnabled != null &&
                                            flowsEnabled > 0
                                                ? 'good'
                                                : 'neutral'
                                        }
                                        name="Processing"
                                        meta={
                                            flowsEnabled == null
                                                ? 'checking…'
                                                : flowsEnabled > 0
                                                ? `${flowsEnabled} flow${
                                                      flowsEnabled === 1
                                                          ? ''
                                                          : 's'
                                                  } enabled`
                                                : 'no flows — captures publish unprocessed'
                                        }
                                        action="Flows"
                                        onGo={() =>
                                            navigate(
                                                `/v2/activations/${id}/processing`,
                                            )
                                        }
                                    />
                                )}
                                {d?.isValid() && (
                                    <div className="mt-3 text-[12px] text-faint">
                                        Doors {d.format('ddd, MMM D')} — rows
                                        update as setup changes.
                                    </div>
                                )}
                            </section>
                            <div ref={devicesRef}>
                                <EventDevices activationId={id} />
                            </div>
                        </>
                    )}

                    {posture === 'live' && (
                        <>
                            {/* the tech's question first: is my booth
                                alive, is the queue moving */}
                            <EventDevices activationId={id} />
                            {chart && (
                                <section className="mb-10">
                                    <div className="ps-label mb-2">
                                        Photos + shares
                                    </div>
                                    <HeroChart
                                        series={chart.series}
                                        axis={chart.axis}
                                        bleed={false}
                                    />
                                </section>
                            )}
                            {mediaStrip.latest.length > 0 && (
                                <section className="mb-10">
                                    <div className="ps-label mb-2">
                                        Latest photos
                                    </div>
                                    <div className="flex gap-2 overflow-x-auto pb-1">
                                        {mediaStrip.latest.map((m: any) => (
                                            <button
                                                key={m.id}
                                                type="button"
                                                title="Open Media"
                                                onClick={() =>
                                                    navigate(
                                                        `/v2/activations/${id}/media`,
                                                    )
                                                }
                                                className="h-[76px] flex-none cursor-pointer overflow-hidden rounded-[6px] border-0 bg-secondary p-0"
                                            >
                                                <img
                                                    src={
                                                        m.thumbnailUri ??
                                                        m.originalUri
                                                    }
                                                    alt=""
                                                    loading="lazy"
                                                    className="h-full w-auto"
                                                />
                                            </button>
                                        ))}
                                    </div>
                                </section>
                            )}
                        </>
                    )}

                    {posture === 'post' && (
                        <>
                            {chart && (
                                <section className="mb-10">
                                    <div className="ps-label mb-2">
                                        How it went
                                    </div>
                                    <HeroChart
                                        series={chart.series}
                                        axis={chart.axis}
                                        bleed={false}
                                    />
                                </section>
                            )}
                            {/* closeout v1: the same device rows — stuck
                                queues already read coral here */}
                            <EventDevices activationId={id} />
                            {mediaStrip.favorites.length > 0 && (
                                <section className="mb-10">
                                    <div className="ps-label mb-2">
                                        Favorites ·{' '}
                                        {mediaStrip.favoriteCount} marked on
                                        Media
                                    </div>
                                    <div className="flex gap-2 overflow-x-auto pb-1">
                                        {mediaStrip.favorites.map(
                                            (m: any) => (
                                                <button
                                                    key={m.id}
                                                    type="button"
                                                    title="Open Media"
                                                    onClick={() =>
                                                        navigate(
                                                            `/v2/activations/${id}/media`,
                                                        )
                                                    }
                                                    className="h-[76px] flex-none cursor-pointer overflow-hidden rounded-[6px] border-0 bg-secondary p-0"
                                                >
                                                    <img
                                                        src={
                                                            m.thumbnailUri ??
                                                            m.originalUri
                                                        }
                                                        alt=""
                                                        loading="lazy"
                                                        className="h-full w-auto"
                                                    />
                                                </button>
                                            ),
                                        )}
                                    </div>
                                </section>
                            )}
                        </>
                    )}
                </div>

                {/* rail — persists across postures; numbers live ONLY here */}
                <aside className="min-w-0">
                    <div className="ps-label mb-3">This event</div>
                    <div className="flex flex-col gap-4">
                        <div>
                            <div className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                                Photos
                                <span className="h-[7px] w-[7px] rounded-full bg-brand" />
                            </div>
                            <div className="mt-1 text-[21px] font-semibold leading-none tracking-[-0.02em] tabular-nums">
                                {kpis.photos.toLocaleString()}
                            </div>
                            {posture === 'pre' && kpis.photos === 0 && (
                                <div className="mt-1 text-[11px] text-faint">
                                    uploads appear at doors
                                </div>
                            )}
                        </div>
                        <div>
                            <div className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                                Sessions
                                <span className="h-[7px] w-[7px] rounded-full bg-faint" />
                            </div>
                            <div className="mt-1 text-[21px] font-semibold leading-none tracking-[-0.02em] tabular-nums">
                                {kpis.sessions.toLocaleString()}
                            </div>
                        </div>
                        {capabilities.sharingOn || kpis.shares > 0 ? (
                            <div>
                                <div className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                                    Shares
                                    <span className="h-[7px] w-[7px] rounded-full bg-attention" />
                                </div>
                                <div className="mt-1 text-[21px] font-semibold leading-none tracking-[-0.02em] tabular-nums">
                                    {kpis.shares.toLocaleString()}
                                </div>
                                {kpis.unsent > 0 && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            navigate(
                                                `/v2/activations/${id}/sharing`,
                                            )
                                        }
                                        className="mt-1 cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11px] font-semibold text-attention"
                                    >
                                        {kpis.unsent.toLocaleString()} unsent
                                        →
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div>
                                <div className="text-[12.5px] text-muted-foreground">
                                    Shares
                                </div>
                                <div className="mt-1 text-[12px] text-muted-foreground">
                                    Sharing is off —{' '}
                                    <button
                                        type="button"
                                        onClick={() =>
                                            navigate(
                                                `/v2/activations/${id}/sharing`,
                                            )
                                        }
                                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] font-semibold text-brand-ink"
                                    >
                                        set up →
                                    </button>
                                </div>
                            </div>
                        )}
                        {capabilities.hasGallery && (
                            <div>
                                <div className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                                    Views
                                    <span className="h-[7px] w-[7px] rounded-full bg-faint" />
                                </div>
                                <div className="mt-1 text-[21px] font-semibold leading-none tracking-[-0.02em] tabular-nums">
                                    {kpis.views.toLocaleString()}
                                </div>
                            </div>
                        )}
                        {rate != null &&
                            (capabilities.sharingOn || kpis.shares > 0) && (
                                <div>
                                    <div className="text-[12.5px] text-muted-foreground">
                                        Share rate
                                    </div>
                                    <div className="mt-1 text-[21px] font-semibold leading-none tracking-[-0.02em] tabular-nums">
                                        {rate}%
                                    </div>
                                </div>
                            )}
                    </div>

                    <div className="ps-label mb-2 mt-9">Gallery links</div>
                    {galleries.length === 0 && (
                        <p className="py-1 text-[13px] text-muted-foreground">
                            No gallery yet.{' '}
                            <button
                                type="button"
                                onClick={() =>
                                    navigate(`/activations/${id}/galleries`)
                                }
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] font-semibold text-brand-ink"
                            >
                                Create one in the classic app →
                            </button>
                        </p>
                    )}
                    {galleries.map((g: any, gi: number) => (
                        <div key={g.id ?? gi}>
                            {[
                                { t: 'Public gallery', path: '' },
                                { t: 'Sharing station', path: '/sharing' },
                                { t: 'Slideshow', path: '/slideshow' },
                                { t: 'Analyze lookup', path: null as any },
                            ].map((link) => {
                                const href = g.slug
                                    ? link.path == null
                                        ? `${galleryUrl}/lookup/${encodeURIComponent(g.slug)}`
                                        : `${galleryUrl}/gallery/${g.slug}${link.path}`
                                    : null;
                                return (
                                    <div
                                        key={link.t}
                                        className="flex items-center gap-2.5 border-b border-line py-[9px] last:border-b-0"
                                    >
                                        <div className="min-w-0">
                                            <div className="text-[12.5px] font-semibold leading-tight">
                                                {link.t}
                                            </div>
                                            <div className="overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[10.5px] text-faint">
                                                {href
                                                    ? href.replace(
                                                          /^https?:\/\//,
                                                          '',
                                                      )
                                                    : '—'}
                                            </div>
                                        </div>
                                        {href && (
                                            <div className="ml-auto flex gap-0.5">
                                                <button
                                                    type="button"
                                                    title={
                                                        copied === link.t
                                                            ? 'Copied'
                                                            : 'Copy'
                                                    }
                                                    onClick={() =>
                                                        copyLink(
                                                            link.t,
                                                            href,
                                                        )
                                                    }
                                                    className={
                                                        'flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-[7px] border-0 bg-transparent hover:bg-secondary hover:text-foreground ' +
                                                        (copied === link.t
                                                            ? 'text-good'
                                                            : 'text-faint')
                                                    }
                                                >
                                                    <IconCopy />
                                                </button>
                                                <a
                                                    href={href}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    title="Open"
                                                    className="flex h-[26px] w-[26px] items-center justify-center rounded-[7px] text-faint hover:bg-secondary hover:text-foreground"
                                                >
                                                    <IconExternal />
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}

                    <div className="ps-label mb-1.5 mt-9">Go deeper</div>
                    <div className="flex flex-col items-start gap-1.5">
                        <button
                            type="button"
                            onClick={() =>
                                navigate(`/v2/activations/${id}/insights`)
                            }
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-muted-foreground hover:text-foreground"
                        >
                            Insights & sessions →
                        </button>
                        <button
                            type="button"
                            onClick={() =>
                                navigate(
                                    `/v2/activations/${id}/sharing/shares`,
                                )
                            }
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-muted-foreground hover:text-foreground"
                        >
                            All shares →
                        </button>
                        <button
                            type="button"
                            onClick={() =>
                                navigate(
                                    `/v2/activations/${id}/payments`,
                                )
                            }
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-muted-foreground hover:text-foreground"
                        >
                            Payments →
                        </button>
                        <button
                            type="button"
                            onClick={copyClientReportLink}
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-muted-foreground hover:text-foreground"
                        >
                            Copy client report link
                        </button>
                    </div>
                </aside>
            </div>

            {/* the activation's job data — schedule, venue, crew, booth,
                gear, contacts, design, comms, timeline */}
            <ActivationOpsSections activationId={id} />
        </main>
    );
}
